/**
 * Shared lobby primitives — small UI building blocks every lobby flow
 * needs (PvPSetup.OnlineSetup, plus the deck-builder draft surfaces).
 * Lives in one file because they're tiny and conceptually related; split
 * out per-component if any of them grow.
 *
 * Exports:
 *   • Nav            — top-of-page brand bar + nav links.
 *   • ConnectionDot  — pulse-dot + Wifi/WifiOff icon for home/away presence.
 *   • ColorPicker    — team-color dropdown w/ optional disabled-key cross-exclusion.
 *   • SortableSlotRow — single roster row inside CreateTab (drag, reroll, remove).
 */

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Wifi, WifiOff, ChevronDown, GripVertical, Info, RefreshCw, X,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { TEAM_COLORS } from "@/components/game/constants";
import { SLOT_POSITIONS } from "@/constants/positions";
import { MIN_SLOTS } from "@/constants/roster";

/* ─── Section label / collapse toggle ────────────────────────────────────── */

// Small label used as a section header inside collapsible panels.
export function SectionLabel({ children }) {
  return <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{children}</p>;
}

// Caret + uppercase label + thin underline. Acts as a button that flips
// `open` for an outer panel.
export function CollapseToggle({ open, onToggle, label }) {
  return (
    <button type="button" onClick={onToggle}
      className="flex items-center gap-1.5 text-[10px] font-black text-slate-600 hover:text-slate-400 uppercase tracking-widest transition-colors w-full">
      <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      {label}
      <span className="flex-1 h-px bg-slate-800 ml-1" />
    </button>
  );
}

/* ─── Nav ────────────────────────────────────────────────────────────────── */

export function Nav() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50">
      <Link to="/" className="flex items-center gap-2 font-black text-lg tracking-tight text-white">
        <img src="/favicons32x32.png" alt="" className="w-5 h-5" />
        JUNK <span className="text-amber-400">BALL</span>
      </Link>
      <div className="flex items-center gap-4">
        <Link to="/print-and-play" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Print & Play</Link>
        <Link to="/contact" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Contact</Link>
        <span className="px-3 py-1.5 rounded-lg bg-amber-500/25 text-amber-900/50 font-bold text-xs cursor-default select-none">Play Now</span>
      </div>
    </nav>
  );
}

/* ─── ConnectionDot ──────────────────────────────────────────────────────── */

export function ConnectionDot({ connected, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${connected ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
      <span className="text-[10px] text-slate-500">{label}</span>
      {connected ? <Wifi size={10} className="text-emerald-400" /> : <WifiOff size={10} className="text-slate-600" />}
    </div>
  );
}

/* ─── ColorPicker ────────────────────────────────────────────────────────── */

// Two-column color dropdown. `disabledKey` is the color the OTHER side has
// claimed — that option renders disabled. `disabled` (whole-picker) is the
// post-ready / away-viewer lock state. Click outside closes the dropdown.
export function ColorPicker({ value, onChange, disabledKey = null, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const dismiss = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [open]);
  const current = TEAM_COLORS[value] || TEAM_COLORS.gold;
  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button type="button" onClick={() => !disabled && setOpen(o => !o)} disabled={disabled}
        title="Choose team color"
        className={`flex items-center gap-1.5 px-2 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 hover:border-slate-500 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
        <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: current.hex }} />
        <ChevronDown size={11} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 rounded-xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden w-44">
          <div className="grid grid-cols-2 p-1.5 gap-0.5">
            {Object.entries(TEAM_COLORS).map(([key, { hex }]) => {
              const isTaken = key === disabledKey;
              return (
                <button key={key} type="button" disabled={isTaken}
                  onClick={() => { if (!isTaken) { onChange(key); setOpen(false); } }}
                  title={isTaken ? "Already chosen by the other team" : undefined}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-bold transition-colors ${
                    isTaken ? "opacity-30 cursor-not-allowed text-slate-600"
                      : value === key ? "bg-white/15 text-white"
                      : "text-slate-400 hover:bg-white/8 hover:text-slate-200"}`}>
                  <span className="w-3.5 h-3.5 rounded-sm flex-shrink-0" style={{ backgroundColor: hex }} />
                  <span className="capitalize">{key}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── SortableSlotRow ────────────────────────────────────────────────────── */

// Single row inside CreateTab's roster list. dnd-kit sortable handle on the
// left, position selector, player name + vintage, inspect/reroll/remove on
// the right. The 8px activation distance on the parent sensor keeps short
// taps from triggering drags.
export function SortableSlotRow({ slot, onRemove, onPositionChange, onReroll, onPreview, canRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: slot.uid });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const empty = !slot.player;
  // Position-eligibility filter for the slot dropdown. When a player is
  // assigned, the dropdown only offers positions that player can legally
  // fill — preventing a natural shortstop (Cal Ripken) from being slotted
  // as "P" by changing the dropdown after the player was placed. With no
  // player, every position is offered. The backend's save endpoint also
  // hard-rejects illegal combos as defense-in-depth.
  const eligiblePositions = (() => {
    // SLOT_POSITIONS, not POSITIONS: DH is a legal slot but not a fielding
    // position, so a DH slot's value matched no option and the control silently
    // displayed the first entry ("P") while the slot stayed DH.
    if (!slot.player) return SLOT_POSITIONS;
    const natural = slot.player.position;
    if (natural === "P") return ["P"];               // pitchers only pitch
    // Non-pitcher: any non-pitcher slot, including DH. Generic "OF" players
    // (where the catalog tags them as outfield without a specific corner)
    // open to all three OF positions plus DH.
    // SLOT_POSITIONS again: built from POSITIONS, which lost "DH", this list
    // could not offer DH, so a DH slot displayed its player's own position.
    const nonPitcher = SLOT_POSITIONS.filter(p => p !== "P");
    if (natural === "OF") return ["LF", "CF", "RF", "DH"];
    // Natural-position match + DH fallback (any non-pitcher can DH).
    return nonPitcher.filter(p => p === natural || p === "DH");
  })();
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 border rounded-lg px-2 py-1.5 ${empty ? "bg-slate-900/40 border-slate-800 border-dashed" : "bg-slate-800/60 border-slate-700/50"}`}>
      <button {...attributes} {...listeners} className="text-slate-600 hover:text-slate-400 cursor-grab active:cursor-grabbing flex-shrink-0">
        <GripVertical size={12} />
      </button>
      <select value={slot.position || ""} onChange={e => onPositionChange(slot.uid, e.target.value)}
        data-testid={`slot-position-${slot.uid}`}
        data-position={slot.position || ""}
        aria-label="Slot position"
        className={`text-[10px] font-black bg-slate-900 border rounded px-1 py-0.5 outline-none flex-shrink-0 w-12 ${
          slot.position ? "border-slate-700 text-amber-400" : "border-amber-500/60 text-slate-500"
        }`}>
        {/* Placeholder for an unset slot. Without a matching option a select
            silently renders its FIRST entry, which is how a DH-defaulted slot
            appeared to say "P" while filling from the DH pool. */}
        {!slot.position && <option value="" disabled>—</option>}
        {eligiblePositions.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <span className={`flex-1 text-xs truncate ${empty ? "text-slate-600 italic" : "text-slate-300"}`}>
        {slot.player?.name || (slot.position ? "Empty — reroll or search" : "Pick a position")}
      </span>
      {slot.player?.year && (
        <span className="text-[9px] text-slate-600 font-mono flex-shrink-0">
          {(() => {
            const seasons = slot.player.seasons || [];
            const rookie = seasons.length > 0 ? seasons[0].year : null;
            const trimmed = slot.player.year;
            return rookie && rookie !== trimmed ? `${rookie} – ${trimmed}` : String(trimmed);
          })()}
        </span>
      )}
      <button onClick={() => slot.player && onPreview(slot.player)} disabled={empty}
        title={empty ? "No player yet" : "View card stats"}
        className="text-slate-600 hover:text-blue-400 disabled:opacity-30 disabled:hover:text-slate-600 transition-colors flex-shrink-0">
        <Info size={12} />
      </button>
      <button onClick={() => onReroll(slot.uid)} title="Reroll this slot"
        className="text-slate-600 hover:text-amber-400 transition-colors flex-shrink-0">
        <RefreshCw size={12} />
      </button>
      <button onClick={() => onRemove(slot.uid)} disabled={!canRemove}
        title={canRemove ? "Remove" : `Need at least ${MIN_SLOTS} players`}
        className="text-slate-600 hover:text-red-400 disabled:opacity-30 disabled:hover:text-slate-600 transition-colors flex-shrink-0">
        <X size={12} />
      </button>
    </div>
  );
}
